import {
  ChargerTypeSelection,
  ChargingLocationSelection,
  ChargingPowerSelection,
  ChargingPowerValues,
  EducationalInfo,
  InputQuestion,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {
  ChargerTypeValues,
  ChargeScenarioOptions,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import React, {useEffect} from 'react';
import {ChargeTimeCalculationFormFields} from '../use-charge-calculation-form';
import {useTenantConfiguration} from '../use-tenant-configuration';
import {useTranslation} from '../use-translation';

interface ChargingSelectionProps {
  chargingLocationField: ChargeTimeCalculationFormFields['chargingLocation'];
  chargerTypeField: ChargeTimeCalculationFormFields['chargerType'];
  chargingPowerField: ChargeTimeCalculationFormFields['chargingPower'];
}

const getChargingPowerOptions = (options: any): number[] => {
  let chargingPower: number[] = [];
  for (const option in options) {
    if (Array.isArray(options[option])) {
      chargingPower.push(...options[option]);
    } else if (
      typeof options[option] === 'object' &&
      options[option] !== null
    ) {
      chargingPower.push(...getChargingPowerOptions(options[option]));
    }
  }
  chargingPower = chargingPower.sort((a, b) => a - b);

  return chargingPower;
};

export const ChargingSelection: React.FC<ChargingSelectionProps> = React.memo(
  ({
    chargingLocationField,
    chargerTypeField,
    chargingPowerField,
  }: ChargingSelectionProps) => {
    const translations = useTranslation();
    const tenantConfig = useTenantConfiguration();

    const chargingPowerOptions = [
      ...new Set(
        getChargingPowerOptions(tenantConfig.resultSection.chargeOptions)
      ),
    ];

    // Select the default charge type when the charging location changes
    useEffect(() => {
      const chargerTypeAndPowerConfiguration =
        tenantConfig.resultSection?.chargeOptions[chargingLocationField.value];
      const validChargerTypeList: ChargerTypeValues[] = [];

      // Iterate over the charger types in the configuration
      for (const chargerType in chargerTypeAndPowerConfiguration) {
        if (
          Object.values(ChargerTypeValues).includes(
            chargerType as ChargerTypeValues
          )
        ) {
          // Check if the array of power options for this charger type is not empty
          const powerOptions =
            chargerTypeAndPowerConfiguration[chargerType as ChargerTypeValues];
          if (powerOptions && powerOptions.length > 0) {
            // Add the charger type to the list of valid charger types
            validChargerTypeList.push(chargerType as ChargerTypeValues);
          }
        }
      }
      const defaultTypeValue =
        chargerTypeAndPowerConfiguration?.defaults?.chargerType ||
        (validChargerTypeList[0] as ChargerTypeValues);

      chargerTypeField.onChange(defaultTypeValue);
    }, [chargingLocationField]);

    // Select the default power option when the charger type changes
    const availablePowerOptions = tenantConfig.resultSection?.chargeOptions[
      chargingLocationField.value
    ][chargerTypeField.value as keyof ChargeScenarioOptions] as number[];

    useEffect(() => {
      if (availablePowerOptions) {
        const defaults =
          tenantConfig.resultSection?.chargeOptions[chargingLocationField.value]
            .defaults;
        const defaultPowerValue =
          defaults?.chargerType === chargerTypeField.value
            ? defaults.power || availablePowerOptions[0]
            : availablePowerOptions[0];

        chargingPowerField.onChange(
          defaultPowerValue as unknown as ChargingPowerValues
        );
      }
    }, [chargingLocationField, chargerTypeField]);

    return (
      <>
        <InputQuestion
          text={translations.inputSection.loadingLocationQuestion.heading}
          questionTextId={chargingLocationField.labelId}
          infoButton={
            <EducationalInfo
              translations={translations.inputSection.loadingLocationQuestion}
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={tenantConfig.inputSection.loadingLocationQuestion}
            />
          }
        >
          <ChargingLocationSelection
            name={chargingLocationField.id}
            questionTextId={chargingLocationField.labelId}
            locationLabelHome={
              translations.inputSection.loadingLocationOptions.home
            }
            locationLabelWork={
              translations.inputSection.loadingLocationOptions.work
            }
            locationLabelPublic={
              translations.inputSection.loadingLocationOptions.public
            }
            locationLabelHighway={
              translations.inputSection.loadingLocationOptions.highway
            }
            value={chargingLocationField.value}
            onChange={chargingLocationField.onChange}
          />
        </InputQuestion>

        <InputQuestion
          text={
            translations.inputSection.loadingChargerTypeQuestions?.heading || ''
          }
          questionTextId={chargerTypeField.labelId}
          infoButton={
            <EducationalInfo
              translations={
                translations.inputSection.loadingChargerTypeQuestions
              }
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={tenantConfig.inputSection.loadingLocationQuestion}
            />
          }
        >
          <ChargerTypeSelection
            name={chargerTypeField.id}
            questionTextId={chargerTypeField.labelId}
            chargerTypeHousehold={
              translations.inputSection.loadingChargerTypeOptions
                ?.householdSocket || ''
            }
            chargerTypeWallbox={
              translations.inputSection.loadingChargerTypeOptions?.wallboxAC ||
              ''
            }
            chargerTypeStationDC={
              translations.inputSection.loadingChargerTypeOptions
                ?.chargingStationDC || ''
            }
            chargingLocation={
              tenantConfig.resultSection.chargeOptions[
                chargingLocationField.value
              ]
            }
            value={chargerTypeField.value}
            onChange={chargerTypeField.onChange}
          />
        </InputQuestion>

        <InputQuestion
          text={
            translations.inputSection.loadingChargingPowerQuestions?.heading ||
            ''
          }
          questionTextId={chargingPowerField.labelId}
          infoButton={
            <EducationalInfo
              translations={
                translations.inputSection.loadingChargingPowerQuestions
              }
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={tenantConfig.inputSection.loadingLocationQuestion}
            />
          }
        >
          <ChargingPowerSelection
            name={chargingPowerField.id}
            questionTextId={chargerTypeField.labelId}
            chargingPower={chargingPowerOptions}
            value={chargingPowerField.value}
            onChange={chargingPowerField.onChange}
            availableChargingPower={availablePowerOptions}
          />
        </InputQuestion>
      </>
    );
  }
);

ChargingSelection.displayName = 'ChargeSelection';
